'use client';
import { FooterProps } from '@/components/footer/types';
import { Link } from '../link/Link';
import { Divider } from '../divider/Divider';
import { Headline } from '../headline/Headline';
import Card from '@/components/card/Card';
import CTACardSignup from '@/components/cta/variants/CTACardSignup';
import { renameDomProps } from '@/utils/sanitizeDomProps';
import { getCTACard } from '@/components/footer/const';
import { useTranslations } from '@/lib/locale-context';
import { BaseImage } from '@/components/baseImage/BaseImage';

export function Footer({
  informativeCardContent,
  navLinks,
  newsletterProps,
  companyLogo,
  privacyLink,
  socialLinks,
  ...rest
}: FooterProps) {
  const renamedProps = renameDomProps(rest);

  const { t, dict } = useTranslations();
  return (
    <footer
      data-component-id="C014"
      className="smzh-footer bg-primary z-100 relative bg-brand-700"
      {...renamedProps}
    >
      <div className="container mx-auto grid w-full grid-cols-4 gap-x-5 gap-y-12 px-4 pt-8 sm:px-0 lg:grid-cols-12 lg:pt-20">
        <div className="col-start-1 col-end-5 lg:col-start-1 lg:col-end-5">
          <Card {...getCTACard(dict)} />
        </div>
        {navLinks.firstSet &&
          navLinks.firstSet.map((section, sectionIndex) => (
            <div
              key={sectionIndex}
              className="smzh-links col-start-1 col-end-3 flex flex-col gap-3 lg:col-start-5 lg:col-end-5 xl:col-start-6 xl:col-end-8"
            >
              {section.title && (
                <Headline
                  level="h6"
                  variant="baseTextM"
                  className="mb-4 text-base font-medium text-white"
                >
                  {section.title}
                </Headline>
              )}
              <div className="flex flex-col gap-2">
                {section.links.map((link, linkIndex) => (
                  <Link key={linkIndex} {...link} theme="dark" />
                ))}
              </div>
            </div>
          ))}
        {navLinks.secondSet &&
          navLinks.secondSet.map((section, sectionIndex) => (
            <div
              key={sectionIndex}
              className="smzh-links col-start-3 col-end-5 flex flex-col gap-3 lg:col-start-7 lg:col-end-9 xl:col-start-8 xl:col-end-10"
            >
              {section.title && (
                <Headline
                  level="h6"
                  variant="baseTextM"
                  className="mb-4 text-base font-medium text-white"
                >
                  {section.title}
                </Headline>
              )}
              <div className="flex flex-col gap-2">
                {section.links.map((link, linkIndex) => (
                  <Link key={linkIndex} {...link} theme="dark" />
                ))}
              </div>
            </div>
          ))}

        <div className="smzh-newsletter col-start-1 col-end-5 !bg-transparent bg-white lg:col-start-9 lg:col-end-13 xl:col-start-10 xl:col-end-13">
          <CTACardSignup
            variant="signUp"
            headline={{
              title: t('newsletter.title') ?? 'Newsletter',
              variant: 'largeTextM',
              level: 'h3',
            }}
            description={[
              {
                type: 'paragraph',
                children: [
                  {
                    text:
                      t('newsletter.description') ??
                      'Verpassen Sie keine News mehr',
                    type: 'text',
                  },
                ],
              },
            ]}
            icon={{
              name: 'mail-check',
              size: '32px',
              color: '#07314C',
            }}
            sectionBackgroundColor="brand-700"
            spacingTop
            spacingBottom
          />
        </div>

        <Divider
          size="small"
          width="full"
          border={true}
          className="col-start-1 col-end-5 grid border-brand-300 opacity-20 lg:col-start-1 lg:col-end-13 lg:grid-cols-12"
        />

        {/* second row */}
        <div className="smzh-card col-start-1 col-end-5 lg:col-start-1 lg:col-end-4">
          <BaseImage
            image={{
              url: '/logo/logo-white.svg',
            }}
            width="192"
            height="60"
            alt="smzh logo"
            objectFit="cover"
            classNameImage="h-auto w-auto"
          />
          <p className="mt-2 text-sm text-white">
            © {new Date().getFullYear()} smzh ag - <br /> All rights reserved.
          </p>
        </div>
        {navLinks.thirdSet &&
          navLinks.thirdSet.map((section, sectionIndex) => (
            <div
              key={sectionIndex}
              className="smzh-links col-start-1 col-end-3 flex flex-col lg:col-start-5 lg:col-end-7 xl:col-start-6 xl:col-end-8"
            >
              <div className="flex flex-col gap-2">
                {section.links.map((link, linkIndex) => (
                  <Link key={linkIndex} {...link} theme="dark" />
                ))}
              </div>
            </div>
          ))}
        <div className="smzh-links col-start-3 col-end-5 lg:col-start-7 lg:col-end-9 xl:col-start-8 xl:col-end-10">
          <div className="smzh-links col-start-3 col-end-5 flex flex-col lg:col-start-8 lg:col-end-10">
            <div className="flex flex-col gap-2">
              <p className="text-white">Tödistrasse 53, CH-8002 Zürich</p>
            </div>
          </div>
        </div>
        <div className="col-start-1 col-end-5 flex flex-row items-start gap-4 md:justify-end lg:col-start-10 lg:col-end-13">
          <Link
            target="_blank"
            href="https://www.facebook.com/smzh.ag/"
            rightIcon={{ name: 'facebook', size: 24 }}
            theme="dark"
          />
          <Link
            target="_blank"
            href="https://www.instagram.com/smzh_ag/"
            rightIcon={{ name: 'instagram', size: 24 }}
            theme="dark"
          />
          <Link
            target="_blank"
            href="https://www.linkedin.com/company/swiss-management-z%C3%BCrich"
            rightIcon={{ name: 'linkedin', size: 24 }}
            theme="dark"
          />
          <Link
            target="_blank"
            href="https://www.whatsapp.com/channel/0029ValBTytJZg47dIXnwG0I"
            rightIcon={{ name: 'whatsapp', size: 24 }}
            theme="dark"
          />
          <Link
            target="_blank"
            href="https://www.youtube.com/@smzh"
            rightIcon={{ name: 'youtube', size: 24 }}
            theme="dark"
          />
          <Link
            target="_blank"
            href="https://www.tiktok.com/@smzh_ag"
            rightIcon={{ name: 'tiktok', size: 24 }}
            theme="dark"
          />
        </div>
        <Divider
          size="small"
          width="full"
          border={false}
          className="col-start-1 col-end-5 lg:col-start-1 lg:col-end-13 lg:grid-cols-12"
        />

        <div className="smzh-gooter-image col-start-1 col-end-5 lg:col-start-1 lg:col-end-13">
          <BaseImage
            className="h-full w-full"
            image={{ url: '/logo/smzh.png' }}
            width="100"
            height="100"
            alt="smzh"
            objectFit="cover"
            classNameImage="h-full w-full"
            sizes="100px"
          />
        </div>
      </div>
    </footer>
  );
}
